import {
  Component,
  Input,
  HostListener,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";

import swal from "sweetalert2";
import { NOTAS_EXCEL } from "../notas-excel";
import { LIMITES } from "../limites";
import { avisarPropuesta, quedoComoPropuesta } from "../propuesta";
import { FechaBitacoraPipe } from "../fecha-bitacora.pipe";
import { ordenReciente } from "../cortes";
import { ProximoMonitoreo, proximoMonitoreo } from "../monitoreo";
import { ExpedienteRiesgoIAComponent } from "../expediente-riesgo-ia/expediente-riesgo-ia.component";
import { HojaMatriz, HojaMatrizBase } from "../matriz-comun/hoja-matriz-base";
import { claseDeCriterio, deEscala } from "../matriz-comun/hoja-criterios";
import { corteDeEvidencia } from "../matriz-comun/hoja-evidencias";
import {
  CatClasificacionSeveridad,
  CatSeveridad,
  Matriz,
  MatrizEvidencia,
  RiesgoProceso,
  RiesgoProcesoHistorial,
  RiesgoProcesoSeguimiento,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";
import { AlertaMonitoreoService } from "../alerta-monitoreo/alerta-monitoreo.service";

/**
 * Hoja "Plan Gestion RSK" en tarjetas: una por riesgo, con la evaluacion
 * inicial y la reevaluacion tras el plan lado a lado, los cortes de monitoreo
 * como linea de tiempo desplegable, y edicion / alta / baja por tarjeta.
 *
 * La tabla de 17 columnas del formato no desaparece: queda como vista previa
 * de solo lectura en pantalla completa, para quien necesita ver la hoja tal
 * como la imprime el Excel oficial.
 */
@Component({
  selector: "app-rsk-completo-ia",
  standalone: true,
  templateUrl: "./rsk-completo-ia.component.html",
  styleUrls: ["./rsk-completo-ia.component.css"],
  imports: [CommonModule, FormsModule, MatTooltipModule, FechaBitacoraPipe, ExpedienteRiesgoIAComponent],
})
export class RskCompletoIAComponent extends HojaMatrizBase<RiesgoProceso, RiesgoProcesoHistorial> {
  private readonly alertaMonitoreo = inject(AlertaMonitoreoService);

  /**
   * Lo que la papelera, las copias y las evidencias dicen de un riesgo. La
   * papelera es la puerta por la que habrían vuelto los cinco RSK que borró
   * la prueba del 09/09.
   */
  protected readonly hoja: HojaMatriz = {
    entidad: "RIESGO",
    origenEvidencia: "RIESGO_PROCESO",
    esteRegistro: "este riesgo",
    vuelveDePapelera: "Vuelve con su folio y sus cortes de seguimiento, tal como estaba ",
    volvioDePapelera: "El riesgo volvió al plan.",
    errorPapelera: "No se pudo restaurar el riesgo.",
    volveraACopia: "El riesgo volverá",
    reemplazaConCopia: "",
    volvioACopia: "El riesgo volvió a la copia.",
  };

  /**
   * Límites de captura, tomados del largo real de cada columna. Van al
   * maxlength de los campos: producción corre en modo estricto y un texto más
   * largo que su columna aborta el guardado en vez de truncarse.
   */
  lim = LIMITES;

  riesgos: RiesgoProceso[] = [];
  severidades: CatSeveridad[] = [];
  clasificaciones: CatClasificacionSeveridad[] = [];
  estrategias: string[] = [];

  cargando = true;

  /** Filtro por clasificacion de la severidad inicial. */
  filtro = "Todas";
  readonly bandas = ["Baja", "Media", "Alta", "Critico"];

  /** Notas del Excel: salen al pasar el mouse en la vista previa. */
  notas = NOTAS_EXCEL;

  /** Copia editable por riesgo. Si el id esta aqui, su tarjeta esta en edicion. */
  borradores: { [id: number]: RiesgoProceso } = {};
  guardando = new Set<number>();

  /** Alta de riesgo. El folio lo asigna el backend al guardar. */
  nuevo: RiesgoProceso | null = null;
  guardandoNuevo = false;

  /** Lineas de tiempo de monitoreo abiertas, por id de riesgo. */
  abiertos = new Set<number>();
  nuevoCorte: { [id: number]: RiesgoProcesoSeguimiento } = {};
  guardandoCorte = new Set<number>();

  /* ---------- Vista previa (pantalla completa) ---------- */

  @HostListener("document:keydown.escape")
  escape(): void {
    // La comparación va primero: es lo que está al frente y es de sólo
    // lectura, así que cerrarla no se lleva nada a medio escribir.
    if (this.comparaRiesgo) {
      this.cerrarComparacion();
      this.cdr.detectChanges();
      return;
    }
    if (this.vistaPrevia) {
      this.alternarVista();
    }
  }

  /* ---------- Carga ---------- */

  cargar(): void {
    this.cargando = true;
    this.cdr.detectChanges();
    // Una sola llamada: trae los riesgos con su seguimiento y los catálogos.
    // Antes se pedía el detalle riesgo por riesgo y eran N peticiones.
    this.service.consultarRiesgosCompleto({ idMatriz: this.idMatriz }).subscribe({
      next: (res) => {
        this.puedeEditar = res && res.puedeEditar === true;
        // Sin sesión —o ante un error de negocio— el backend responde 200 con
        // el cuerpo vacío: sin esta guarda, la pantalla se quedaba en
        // "Cargando…" para siempre.
        if (!res) {
          this.cargando = false;
          this.cdr.detectChanges();
          swal.fire("No se pudo cargar", "Vuelve a intentarlo o recarga la página.", "warning");
          return;
        }
        this.severidades = res.severidades || [];
        this.clasificaciones = res.clasificaciones || [];
        this.estrategias = res.estrategias || [];
        const lista = res.riesgos || [];
        // Igual que en Activos: sin seguimientos definido = detalle no cargado.
        this.finCarga(lista, lista.filter((r) => !r.seguimientos).length);
      },
      error: (e) => {
        console.error("Error al consultar riesgos de proceso", e);
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  private finCarga(lista: RiesgoProceso[], fallidos = 0): void {
    lista.sort((a, b) => (a.consecutivo || "").localeCompare(b.consecutivo || ""));
    this.riesgos = lista;
    // Se conserva lo que el usuario tenga a medio capturar en otras tarjetas:
    // cada guardado recarga, y borrar parejo se llevaria esos borradores.
    const anteriores = this.nuevoCorte;
    this.nuevoCorte = {};
    lista.forEach((r) => {
      if (r.idRiesgoProceso) {
        this.nuevoCorte[r.idRiesgoProceso] =
          anteriores[r.idRiesgoProceso] || { fechaMonitoreo: "", resultado: "" };
      }
    });
    this.cargando = false;
    this.cdr.detectChanges();
    if (fallidos > 0) {
      swal.fire(
        "Carga incompleta",
        "No se pudo traer el detalle de " + fallidos + " riesgo(s); " +
          "se muestran sin sus cortes de monitoreo. Recarga para reintentar.",
        "warning"
      );
    }
  }

  /* ---------- Filtro por severidad ---------- */

  get filtrados(): RiesgoProceso[] {
    if (this.filtro === "Todas") {
      return this.riesgos;
    }
    return this.riesgos.filter((r) => r.clasificacionInicial === this.filtro);
  }

  conteo(banda: string): number {
    return this.riesgos.filter((r) => r.clasificacionInicial === banda).length;
  }

  /** La clasificacion viaja sin acento ("Critico"); en pantalla va con el. */
  etiquetaBanda(c?: string): string {
    return c === "Critico" ? "Crítico" : c || "";
  }

  /* ---------- Catalogos y calculo en vivo ---------- */

  severidadPor(aplicaA: string): CatSeveridad[] {
    return deEscala(this.severidades, aplicaA);
  }

  etiqueta(aplicaA: string, nivel?: number): string {
    if (nivel === null || nivel === undefined) {
      return "";
    }
    const s = this.severidades.find((x) => x.aplicaA === aplicaA && x.nivel === nivel);
    return s && s.etiqueta ? s.etiqueta : String(nivel);
  }

  /** Severidad en vivo mientras se edita. El backend la recalcula al guardar. */
  severidad(p?: number, i?: number): number | null {
    return p && i ? p * i : null;
  }

  /** Banda de una severidad calculada en vivo, con el catalogo del backend. */
  bandaViva(n: number | null): string {
    if (!n) {
      return "";
    }
    const c = this.clasificaciones.find(
      (x) =>
        x.nivelDesde !== undefined &&
        x.nivelHasta !== undefined &&
        n >= x.nivelDesde! &&
        n <= x.nivelHasta!
    );
    if (c && c.clasificacion) {
      return c.clasificacion;
    }
    return n <= 4 ? "Baja" : n <= 8 ? "Media" : n <= 12 ? "Alta" : "Critico";
  }

  claseSeveridad(c?: string): string {
    switch (c) {
      case "Critico": return "s-critico";
      case "Alta": return "s-alta";
      case "Media": return "s-media";
      case "Baja": return "s-baja";
      default: return "";
    }
  }

  /** Cuantos puntos bajo la severidad tras el plan. Negativo = subio. */
  mejora(r: RiesgoProceso): number | null {
    if (r.severidadInicial == null || r.severidadReevaluada == null) {
      return null;
    }
    return r.severidadInicial - r.severidadReevaluada;
  }

  listaActivos(r: RiesgoProceso): string[] {
    if (!r.activosRelacionados) {
      return [];
    }
    return r.activosRelacionados.split(",").map((x) => x.trim()).filter((x) => !!x);
  }

  /**
   * La descripción del FOS-005 termina en "(activos evaluados: IA-0001, …)",
   * que aquí ya se muestra como etiquetas aparte. Se quita del texto sólo si
   * cada folio citado está en esas etiquetas: si no coinciden, no se toca,
   * porque entonces el paréntesis dice algo que el campo no dice.
   *
   * Es limpieza de pantalla; el dato guardado no cambia.
   */
  descripcionLimpia(r: RiesgoProceso): string {
    const texto = (r.descripcion || "").trim();
    // Sin \s* al principio ni tras los dos puntos: hacían retroceder de más
    // la búsqueda. Los espacios que ya no atrapa los quitan los trim() de abajo.
    const cola = /\(activos evaluados:([^)]*)\)\s*$/i.exec(texto);
    if (!cola || cola.index === undefined) {
      return texto;
    }
    const citados = cola[1].split(",").map((x) => x.trim()).filter((x) => !!x);
    const etiquetas = this.listaActivos(r);
    if (citados.length === 0 || etiquetas.length === 0) {
      return texto;
    }
    const todos = citados.every((f) => etiquetas.indexOf(f) >= 0);
    return todos ? texto.slice(0, cola.index).trim() : texto;
  }

  /* ---------- Criterios de evaluación ---------- */

  /**
   * La tabla de criterios del formato: probabilidad, impacto y las bandas de
   * severidad con su criterio de aceptación.
   *
   * No lleva datos propios: se arma con los catálogos que la pantalla ya tiene
   * cargados (severidades y clasificaciones), que son los mismos con los que el
   * backend calcula. Así no puede desincronizarse del cálculo real.
   */
  criterios = false;

  abrirCriterios(): void {
    this.criterios = true;
  }

  cerrarCriterios(): void {
    this.criterios = false;
  }

  /** Color de la banda en la tabla de criterios, como en el formato (hoja-criterios.ts). */
  claseCriterio(c?: string): string {
    return claseDeCriterio(c);
  }

  /** "1 a 4", tomado del propio catálogo. */
  rangoBanda(c: CatClasificacionSeveridad): string {
    return (c.nivelDesde ?? "") + " a " + (c.nivelHasta ?? "");
  }

  /* ---------- Bitácora ---------- */

  /**
   * Modo lectura. Lo decide el backend por perfil (sólo cinco personas
   * editan el plan de gestión) y aquí sólo esconde botones: el candado de
   * verdad está en el servidor.
   */
  puedeEditar = false;

  /**
   * El backend guarda el nombre físico de la columna. Si se agrega un campo al
   * diff de RiesgoProcesoBuss, hay que agregarlo también aquí.
   */
  private static readonly ETIQUETAS: { [k: string]: string } = {
    fase_deteccion: "Fase de detección",
    descripcion: "Descripción del riesgo",
    causa_raiz: "Causa raíz",
    prob_inicial: "Probabilidad al detectarlo",
    impacto_inicial: "Impacto al detectarlo",
    estrategia: "Estrategia",
    fecha_identificacion: "Fecha de identificación",
    periodicidad: "Periodicidad de monitoreo",
    acciones: "Plan de acción",
    responsable_recursos: "Responsable y recursos",
    prob_reevaluada: "Probabilidad tras el plan",
    impacto_reevaluado: "Impacto tras el plan",
    activos_relacionados: "Activos evaluados",
    estatus: "Estatus",
    seguimiento: "Monitoreo",
    // Las evidencias no cambian el riesgo, pero SI son parte del expediente:
    // quien adjunto el respaldo y cuando es justo lo que pide la auditoria.
    evidencia: "Evidencia adjunta",
    alta: "Alta del riesgo",
    eliminacion: "Eliminación",
  };

  /* ---------- Lo que pide la base (matriz-comun/hoja-matriz-base.ts) ---------- */

  /** El riesgo cuya comparación está abierta, o null si está cerrada. */
  get comparaRiesgo(): RiesgoProceso | null {
    return this.comparaRegistro;
  }

  protected idDe(r: RiesgoProceso): number | undefined {
    return r.idRiesgoProceso;
  }

  /** El alta de riesgo también se dibuja a pantalla completa. */
  protected formularioAbierto(): boolean {
    return !!this.nuevo;
  }

  /** Después de regresar a una copia se relee el plan entero. */
  protected trasRestaurarCopia(): void {
    this.cargar();
  }

  /**
   * Las listas que dependen de lo que acaba de pasar en el servidor. Cada
   * guardado y cada corte dejan una copia nueva; si no se relee, la copia
   * recién tomada no aparece hasta recargar la página.
   */
  private refrescarListas(id: number): void {
    delete this.respaldos[id];
    delete this.bitacora[id];
    if (this.respaldosAbiertos.has(id)) {
      this.cargaRespaldos(id);
    }
  }

  alternarBitacora(r: RiesgoProceso): void {
    const id = r.idRiesgoProceso!;
    if (this.bitacoraAbierta.has(id)) {
      this.bitacoraAbierta.delete(id);
      return;
    }
    this.bitacoraAbierta.add(id);
    if (this.bitacora[id]) {
      return;
    }
    this.cargandoBitacora.add(id);
    this.service.historialRiesgo(id).subscribe({
      next: (res) => {
        this.cargandoBitacora.delete(id);
        this.bitacora[id] = (res && res.historial) || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoBitacora.delete(id);
        this.bitacora[id] = [];
        this.cdr.detectChanges();
      },
    });
  }

  campo(h: RiesgoProcesoHistorial): string {
    if (!h.campoModificado) {
      return "";
    }
    return RskCompletoIAComponent.ETIQUETAS[h.campoModificado] || h.campoModificado;
  }

  /* ---------- Evidencias adjuntas del riesgo ----------
   * Mismo patrón que en los activos de la matriz: imagen, PDF, Excel...
   * como respaldo del riesgo y su tratamiento; se piden al desplegar. */

  /* ---------- Gestión de Riesgos ---------- */
  /** Si su matriz trabaja con activos: sólo ahí tiene sentido relacionarlos. */
  @Input() conActivos = true;
  /** La cabecera de la matriz: de ahí sale su nombre y si maneja activos. */
  @Input() matriz: Matriz | null = null;

  /* ---------- Expediente del riesgo ----------
   * Sólo en las matrices que NO trabajan con activos. En las que sí —hoy la
   * de IA— el expediente vive en la pestaña de Activos, colgado del activo,
   * que es donde está toda la trazabilidad: inventario, monitoreos e
   * incidencias. Ahí este botón sobraría y por eso se quitó en su momento.
   *
   * En una matriz sin activos no hay a qué colgarlo, y el riesgo del Plan de
   * Gestión es la única unidad que se puede documentar. */
  expediente: RiesgoProceso | null = null;

  abrirExpediente(r: RiesgoProceso): void {
    this.expediente = r;
  }

  cerrarExpediente(): void {
    this.expediente = null;
    this.cdr.detectChanges();
  }

  /** A qué corte de monitoreo respalda la evidencia que se va a subir. */
  corteEvidencia: { [id: number]: number | null } = {};

  subirEvidencia(r: RiesgoProceso, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    input.value = "";
    // El [disabled] del HTML es la cortesia; esta guarda es la que de verdad
    // protege: el atributo se quita desde el inspector en dos clics.
    if (!this.enEdicion(r)) {
      return;
    }
    if (!archivo || !r.idRiesgoProceso) {
      return;
    }
    const id = r.idRiesgoProceso;
    this.subiendoEvidencia.add(id);
    this.vigilaSubida(id, this.service.subirEvidencia(archivo, "RIESGO_PROCESO", id, undefined,
                                                      this.corteEvidencia[id]));
  }

  eliminarEvidencia(r: RiesgoProceso, evd: MatrizEvidencia): void {
    // El [disabled] del HTML es la cortesia; esta guarda es la que de verdad
    // protege: el atributo se quita desde el inspector en dos clics.
    if (!this.enEdicion(r)) {
      return;
    }
    swal.fire({
      title: "¿Eliminar la evidencia?",
      text: (evd.nombreArchivo || "") + " — se borra el archivo del servidor y no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((resp) => {
      if (!resp.isConfirmed || !evd.idEvidencia || !r.idRiesgoProceso) {
        return;
      }
      this.service.eliminarEvidencia(evd.idEvidencia).subscribe((res) => {
        if (res && res.codigoError) {
          swal.fire("No se pudo eliminar", res.mensajeError || "", "error");
        }
        this.cargarEvidencias(r.idRiesgoProceso!);
      });
    });
  }

  /**
   * "No aplica": la sección se esconde y queda una línea discreta con el
   * botón para revertir. El backend rechaza NO si ya hay archivos.
   */
  marcarNoAplicaEvidencias(r: RiesgoProceso): void {
    // El [disabled] del HTML es la cortesia; esta guarda es la que de verdad
    // protege: el atributo se quita desde el inspector en dos clics.
    if (!this.enEdicion(r)) {
      return;
    }
    swal.fire({
      title: "¿Marcar evidencias como No aplica?",
      text: "La sección se ocultará para " + (r.consecutivo || "este riesgo") +
        " y quedará como estado declarado, no como un faltante. Se puede revertir cuando quieras.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, no aplica",
      cancelButtonText: "Cancelar",
    }).then((resp) => {
      if (resp.isConfirmed && r.idRiesgoProceso) {
        this.guardarAplicaEvidencias(r.idRiesgoProceso, "NO");
      }
    });
  }

  marcarSiAplicaEvidencias(r: RiesgoProceso): void {
    // El [disabled] del HTML es la cortesia; esta guarda es la que de verdad
    // protege: el atributo se quita desde el inspector en dos clics.
    if (!this.enEdicion(r)) {
      return;
    }
    if (r.idRiesgoProceso) {
      this.guardarAplicaEvidencias(r.idRiesgoProceso, "SI");
    }
  }

  /** "Corte del 13/07/2026", para pintarlo junto a cada archivo. */
  corteDe(evd: MatrizEvidencia): string {
    return corteDeEvidencia(evd);
  }

  /** Sólo se llama cuando el movimiento no trae usuario. */
  quien(h: RiesgoProcesoHistorial): string {
    return h.idUsuario === 0 ? "Carga inicial desde el Excel" : "Sin usuario registrado";
  }

  claseAccion(accion?: string): string {
    switch (accion) {
      case "ALTA": return "a-alta";
      case "BAJA": return "a-baja";
      default: return "a-mod";
    }
  }

  /* ---------- Tarjetas contraídas ---------- */

  /**
   * Las tarjetas nacen contraidas: con el detalle completo de cada riesgo la
   * lista obliga a mucho scroll para comparar. Se guarda lo ABIERTO, que es lo
   * excepcional.
   */
  expandidos = new Set<number>();

  /** En edicion siempre abierta: no tendria sentido capturar a ciegas. */
  expandido(r: RiesgoProceso): boolean {
    return this.expandidos.has(r.idRiesgoProceso!) || this.enEdicion(r);
  }

  alternarTarjeta(r: RiesgoProceso): void {
    const id = r.idRiesgoProceso!;
    if (this.expandidos.has(id)) {
      this.expandidos.delete(id);
    } else {
      this.expandidos.add(id);
      // La bandera "aplica / no aplica" de evidencias se necesita en cuanto
      // el detalle es visible, para pintar la sección o su línea de No aplica.
      if (this.evidenciaAplica[id] === undefined) {
        this.cargarEvidencias(id);
      }
    }
  }

  /* ---------- Llegada desde el resumen de General ---------- */

  /** Tarjeta a la que se acaba de saltar; se resalta un momento y se apaga. */
  enfocado: number | null = null;

  /**
   * La llama el libro al dar clic en un renglon del resumen. Quita el filtro
   * antes de buscar la tarjeta: con una banda seleccionada podria no estar
   * en pantalla y el scroll no encontraria nada.
   */
  enfocar(id?: number): void {
    if (!id) {
      return;
    }
    this.filtro = "Todas";
    this.expandidos.add(id);
    this.enfocado = id;
    this.cdr.detectChanges();
    const el = document.getElementById("rsk-" + id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => {
      this.enfocado = null;
      this.cdr.detectChanges();
    }, 2400);
  }

  /* ---------- Edicion por tarjeta ---------- */

  enEdicion(r: RiesgoProceso): boolean {
    return !!(r.idRiesgoProceso && this.borradores[r.idRiesgoProceso]);
  }

  borrador(r: RiesgoProceso): RiesgoProceso {
    return this.borradores[r.idRiesgoProceso!];
  }

  editar(r: RiesgoProceso): void {
    if (this.bloqueado(r)) {
      return;
    }
    const copia: RiesgoProceso = JSON.parse(JSON.stringify(r));
    delete copia.seguimientos;
    this.expandidos.add(r.idRiesgoProceso!);
    this.borradores[r.idRiesgoProceso!] = copia;
  }

  /* ---------- Una sola edicion abierta a la vez ---------- */

  /**
   * El riesgo que hoy tiene algo abierto: la tarjeta en edicion o uno de sus
   * cortes. Con dos tarjetas abiertas a la vez es facil guardar la equivocada.
   */
  private riesgoEditandose(): number | null {
    const enTarjeta = Object.keys(this.borradores);
    if (enTarjeta.length) {
      return Number(enTarjeta[0]);
    }
    const enCorte = Object.keys(this.corteEditando);
    if (enCorte.length) {
      const c = this.corteEditando[Number(enCorte[0])];
      return c && c.idRiesgoProceso != null ? c.idRiesgoProceso : null;
    }
    return null;
  }

  /** true cuando OTRA tarjeta tiene una edicion abierta, o se esta dando de alta. */
  bloqueado(r: RiesgoProceso): boolean {
    if (this.nuevo) {
      return true;
    }
    const abierto = this.riesgoEditandose();
    return abierto !== null && abierto !== r.idRiesgoProceso;
  }

  /** true si alguna tarjeta tiene una edicion abierta, o se esta dando de alta. */
  get hayEdicionAbierta(): boolean {
    return !!this.nuevo || this.riesgoEditandose() !== null;
  }

  /**
   * Por que no se puede dar de alta otro riesgo ahora. Con una tarjeta abierta
   * y un alta nueva al mismo tiempo es facil guardar la equivocada.
   */
  avisoAgregar(): string {
    if (this.nuevo) {
      return "Ya hay un riesgo nuevo sin guardar.";
    }
    return this.riesgoEditandose() !== null ? this.avisoBloqueo() : "";
  }

  /**
   * Por que esta apagada una accion de ADENTRO de la tarjeta (subir o borrar
   * una evidencia, marcar si aplica). Nada de lo que cambia el riesgo se toca
   * de pasada: primero hay que entrar a editarlo con el boton de arriba.
   *
   * Si ademas hay OTRA tarjeta abierta, se dice eso, que es lo que de verdad
   * le estorba al usuario en ese momento.
   */
  avisoSoloEdicion(r: RiesgoProceso): string {
    if (this.enEdicion(r)) {
      return "";
    }
    if (this.bloqueado(r)) {
      return this.avisoBloqueo();
    }
    const folio = r.consecutivo ? r.consecutivo : "el riesgo";
    return "Da clic en Editar (arriba, en " + folio + ") para poder cambiar esto.";
  }

  /** Por que esta bloqueada la tarjeta. Sale como title del boton apagado. */
  avisoBloqueo(): string {
    if (this.nuevo) {
      return "Termina primero el riesgo nuevo: guárdalo o cancélalo.";
    }
    const id = this.riesgoEditandose();
    if (id === null) {
      return "";
    }
    const r = this.riesgos.find((x) => x.idRiesgoProceso === id);
    const folio = r && r.consecutivo ? r.consecutivo : "el riesgo abierto";
    return "Termina primero " + folio + ": guárdalo o cancélalo.";
  }

  /**
   * Al cerrar la tarjeta —guardando o cancelando— se cierran tambien los cortes
   * que se hayan abierto dentro de ella.
   *
   * Si no, riesgoEditandose() los sigue viendo en corteEditando y TODA la
   * pantalla queda bloqueada por un formulario que el usuario cree haber
   * cancelado. Y no puede rescatarlo: el bloque del corte cuelga de
   * *ngIf="enEdicion(r)", asi que al cerrar la tarjeta desaparece de pantalla
   * sin cerrarse, y la unica salida es recargar la pagina.
   */
  private cerrarCortesDe(id: number): void {
    Object.keys(this.corteEditando).forEach((k) => {
      const c = this.corteEditando[Number(k)];
      if (c && c.idRiesgoProceso === id) {
        delete this.corteEditando[Number(k)];
      }
    });
  }

  cancelarEdicion(r: RiesgoProceso): void {
    this.cerrarCortesDe(r.idRiesgoProceso!);
    delete this.borradores[r.idRiesgoProceso!];
  }

  guardarEdicion(r: RiesgoProceso): void {
    const id = r.idRiesgoProceso!;
    const b = this.borradores[id];
    if (!b || !b.descripcion || !b.descripcion.trim()) {
      swal.fire("Falta la descripción", "Describe el riesgo antes de guardar.", "warning");
      return;
    }
    this.guardando.add(id);
    this.cdr.detectChanges();
    this.service.actualizarRiesgo(b).subscribe({
      next: (res) => {
        this.guardando.delete(id);
        if (!res || res.codigoError) {
          swal.fire("No se guardó", (res && res.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        this.cerrarCortesDe(id);
        delete this.borradores[id];
        // Con el control de cambios encendido no se guardó: quedó propuesto.
        // Se cierra el borrador igual y se relee, que sigue como estaba.
        if (quedoComoPropuesta(res)) {
          void avisarPropuesta("El cambio de " + (r.consecutivo || "este riesgo"));
        }
        // El servidor acaba de guardar una copia: la lista de versiones se
        // relee, o la recién tomada no aparece hasta recargar la página.
        this.refrescarListas(id);
        this.cargar();
      },
      error: () => {
        this.guardando.delete(id);
        swal.fire("Error", "No se pudo guardar el riesgo.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Alta ---------- */

  agregarRiesgo(): void {
    // Con una tarjeta abierta y un alta al mismo tiempo es facil guardar la
    // equivocada. El boton ya sale apagado; esto lo cierra de verdad.
    if (this.hayEdicionAbierta) {
      return;
    }
    this.filtro = "Todas";
    this.nuevo = {
      // El campo ya no se captura en pantalla. Se sigue guardando como "NA",
      // que es lo que vale en todos los riesgos del formato, para que la hoja
      // FOS-005 exportada no salga con esa columna vacía.
      faseDeteccion: "NA",
      estrategia: this.estrategias.indexOf("Mitigar") >= 0 ? "Mitigar" : undefined,
      periodicidad: "Cuatrimestral (cada 4 meses)",
      fechaIdentificacion: this.hoy(),
    };
    // El alta toma la pantalla completa, igual que el editor de Activos: es un
    // formulario largo y capturarlo encajado entre las tarjetas obliga a
    // desplazarse para ver lo que se esta escribiendo.
    this.sincronizarPantalla();
  }

  cancelarNuevo(): void {
    this.nuevo = null;
    this.sincronizarPantalla();
  }

  guardarNuevo(): void {
    if (!this.nuevo || !this.nuevo.descripcion || !this.nuevo.descripcion.trim()) {
      swal.fire("Falta la descripción", "Describe el riesgo antes de guardar.", "warning");
      return;
    }
    this.guardandoNuevo = true;
    this.cdr.detectChanges();
    this.nuevo.idMatriz = this.idMatriz;
    this.service.guardarRiesgo(this.nuevo).subscribe({
      next: (res) => {
        this.guardandoNuevo = false;
        if (!res || res.codigoError) {
          swal.fire("No se guardó", (res && res.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        this.nuevo = null;
        this.sincronizarPantalla();
        // Propuesto: todavía no tiene folio ni aparece en la lista.
        if (quedoComoPropuesta(res)) {
          void avisarPropuesta("El alta del riesgo");
        } else {
          swal.fire("Listo", "El riesgo se agregó con su folio consecutivo.", "success");
        }
        this.cargar();
      },
      error: () => {
        this.guardandoNuevo = false;
        swal.fire("Error", "No se pudo guardar el riesgo.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Baja ---------- */

  eliminar(r: RiesgoProceso): void {
    if (this.bloqueado(r)) {
      return;
    }
    const cortes = r.seguimientos ? r.seguimientos.length : 0;
    this.confirma = {
      titulo: "¿Eliminar " + (r.consecutivo || "este riesgo") + "?",
      texto:
        cortes > 0
          ? "Se borra el riesgo junto con sus " + cortes + " cortes de monitoreo."
          : "Se borra el riesgo de la matriz.",
      detalle:
        "Se guarda una copia antes de borrarlo: si hace falta, vuelve con su folio " +
        "desde el botón «Eliminados».",
      botonSi: "Sí, eliminar",
      accion: () => this.eliminarConfirmado(r),
    };
  }

  private eliminarConfirmado(r: RiesgoProceso): void {
    if (!r.idRiesgoProceso) {
      return;
    }
    this.service.eliminarRiesgo(r.idRiesgoProceso).subscribe({
      next: (d) => {
        if (!d || d.codigoError) {
          swal.fire("No se eliminó", (d && d.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        // Propuesta: el riesgo sigue en la matriz hasta que se acepte.
        if (quedoComoPropuesta(d)) {
          void avisarPropuesta("La eliminación de " + (r.consecutivo || "este riesgo"));
        }
        this.cargar();
        if (this.papeleraAbierta) {
          this.cargaPapelera();
        }
      },
      error: () => {
        swal.fire("Error", "No se pudo eliminar el riesgo.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Monitoreo ---------- */

  alternarCortes(r: RiesgoProceso): void {
    const id = r.idRiesgoProceso!;
    if (this.abiertos.has(id)) {
      this.abiertos.delete(id);
    } else {
      this.abiertos.add(id);
    }
  }

  /**
   * La línea del monitoreo, del corte más reciente al más antiguo
   * (29/09/2026): el primero es el último monitoreo y va marcado.
   */
  cortesRecientes(r: RiesgoProceso): RiesgoProcesoSeguimiento[] {
    return ordenReciente(r.seguimientos, (s) => s.idRpSeguimiento);
  }

  /**
   * Cuándo le toca el siguiente monitoreo (29/09/2026): el último corte más
   * su periodicidad. Avisa desde 5 días antes (monitoreo.ts).
   */
  proximo(r: RiesgoProceso): ProximoMonitoreo {
    const ultimo = this.cortesRecientes(r)[0];
    return proximoMonitoreo(r.periodicidad, ultimo && ultimo.fechaMonitoreo, r.fechaIdentificacion);
  }

  /** Cortes que se migraron sin resultado desde el Excel. */
  esPendiente(resultado?: string): boolean {
    return !!resultado && resultado.indexOf("NA - el resultado de este corte") === 0;
  }

  /* ---------- La linea de tiempo no camina hacia atras ---------- */


  /**
   * Desde cuando se puede fechar un corte NUEVO: el mas tarde entre hoy y el
   * ultimo corte registrado. Ni hacia atras en la linea de tiempo, ni con
   * fecha pasada — un corte dice lo que se encontro, y se captura al hacerlo.
   */
  minNuevoCorte(r: RiesgoProceso): string {
    const ultima = this.ultimaFechaCorte(r);
    const hoy = this.hoy();
    return ultima > hoy ? ultima : hoy;
  }

  /**
   * Fecha del ultimo corte registrado, en el formato del <input type="date">.
   * Un corte nuevo no puede quedar antes: la linea de tiempo se leeria al reves
   * y en el formato FOS-005 las parejas de columnas van en orden.
   */
  ultimaFechaCorte(r: RiesgoProceso): string {
    let max = "";
    (r.seguimientos || []).forEach((s) => {
      const f = this.aFecha(s.fechaMonitoreo);
      if (f > max) {
        max = f;
      }
    });
    return max;
  }

  /** Al corregir un corte tampoco puede pasarse antes del corte que lo precede. */
  minEdicionCorte(r: RiesgoProceso, s: RiesgoProcesoSeguimiento): string {
    const propia = this.aFecha(s.fechaMonitoreo);
    let max = "";
    (r.seguimientos || []).forEach((x) => {
      if (x.idRpSeguimiento === s.idRpSeguimiento) {
        return;
      }
      const f = this.aFecha(x.fechaMonitoreo);
      if (f < propia && f > max) {
        max = f;
      }
    });
    return max;
  }

  /** "2026-07-13" -> "13/07/2026", sin pasar por Date para no correr el dia. */
  private aDia(f: string): string {
    const p = (f || "").split("-");
    return p.length === 3 ? p[2] + "/" + p[1] + "/" + p[0] : f;
  }

  /* ---------- Confirmacion propia, centrada ---------- */

  /**
   * Sustituye al confirm de la libreria: en esta pantalla conviven la vista
   * previa a pantalla completa y los paneles del CDK, y el modal propio se
   * coloca por encima de los dos. Se cierra solo con sus botones.
   */
  confirma: {
    titulo: string;
    texto: string;
    detalle?: string;
    botonSi: string;
    accion: () => void;
  } | null = null;

  confirmarNo(): void {
    this.confirma = null;
  }

  confirmarSi(): void {
    const hacer = this.confirma ? this.confirma.accion : null;
    this.confirma = null;
    if (hacer) {
      hacer();
    }
  }

  /* ---------- Parejas de columnas de monitoreo (vista previa) ---------- */

  /**
   * En el formato, cada ciclo de monitoreo agrega una pareja de columnas
   * —Fecha de Monitoreo y reevaluación, y Resultado— y así sucesivamente. El
   * número de parejas lo manda el riesgo que más cortes lleva.
   */
  get paresMonitoreo(): number[] {
    let max = 0;
    this.riesgos.forEach((r) => {
      const n = r.seguimientos ? r.seguimientos.length : 0;
      if (n > max) {
        max = n;
      }
    });
    const pares: number[] = [];
    for (let i = 0; i < max; i++) {
      pares.push(i);
    }
    return pares;
  }

  /** El corte del ciclo i, o null si ese riesgo todavía no llega a ese ciclo. */
  corte(r: RiesgoProceso, i: number): RiesgoProcesoSeguimiento | null {
    return r.seguimientos && r.seguimientos[i] ? r.seguimientos[i] : null;
  }

  /** 17 columnas base (2096px) más 320px por cada pareja de monitoreo. */
  get anchoFormato(): string {
    return 2096 + 320 * this.paresMonitoreo.length + "px";
  }

  pendientes(r: RiesgoProceso): number {
    if (!r.seguimientos) {
      return 0;
    }
    return r.seguimientos.filter((s) => this.esPendiente(s.resultado)).length;
  }

  /** Cuántos cortes de toda la hoja siguen sin resultado. */
  get totalPendientes(): number {
    return this.riesgos.reduce((n, r) => n + this.pendientes(r), 0);
  }

  /** Los folios que los tienen, para nombrarlos en el aviso. */
  get foliosPendientes(): string {
    return this.riesgos
      .filter((r) => this.pendientes(r) > 0)
      .map((r) => r.consecutivo || "")
      .join(", ");
  }

  /* ---------- Corregir un corte ---------- */

  /** Copia editable del corte que se está corrigiendo, por id. */
  corteEditando: { [id: number]: RiesgoProcesoSeguimiento } = {};
  guardandoEdicionCorte = new Set<number>();

  editandoCorte(s: RiesgoProcesoSeguimiento): boolean {
    return !!(s.idRpSeguimiento && this.corteEditando[s.idRpSeguimiento]);
  }

  borradorCorte(s: RiesgoProcesoSeguimiento): RiesgoProcesoSeguimiento {
    return this.corteEditando[s.idRpSeguimiento!];
  }

  editarCorte(r: RiesgoProceso, s: RiesgoProcesoSeguimiento): void {
    // El [disabled] del HTML es la cortesia; esta guarda es la que de verdad
    // protege: el atributo se quita desde el inspector en dos clics.
    if (!this.enEdicion(r)) {
      return;
    }
    // Sobre una copia: si se cancela, el corte no queda tocado.
    this.corteEditando[s.idRpSeguimiento!] = {
      idRpSeguimiento: s.idRpSeguimiento,
      idRiesgoProceso: s.idRiesgoProceso,
      fechaMonitoreo: this.aFecha(s.fechaMonitoreo),
      resultado: s.resultado,
    };
  }

  cancelarEdicionCorte(s: RiesgoProcesoSeguimiento): void {
    delete this.corteEditando[s.idRpSeguimiento!];
  }

  guardarEdicionCorte(s: RiesgoProcesoSeguimiento): void {
    const id = s.idRpSeguimiento!;
    const b = this.corteEditando[id];
    if (!b) {
      return;
    }
    if (!b.resultado || !b.resultado.trim()) {
      swal.fire(
        "Falta el resultado",
        "El resultado es obligatorio. Si no se realizó la revisión, escribe NA y la razón.",
        "warning"
      );
      return;
    }
    // La fecha no se edita: se manda siempre la del registro original.
    b.fechaMonitoreo = this.aFecha(s.fechaMonitoreo);
    this.guardandoEdicionCorte.add(id);
    this.cdr.detectChanges();
    this.service.actualizarSeguimientoRiesgo(b).subscribe({
      next: (d) => {
        this.guardandoEdicionCorte.delete(id);
        if (!d || d.codigoError) {
          swal.fire("No se guardó", (d && d.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        delete this.corteEditando[id];
        if (quedoComoPropuesta(d)) {
          void avisarPropuesta("La corrección del corte");
        }
        this.refrescarListas(id);
        this.cargar();
      },
      error: () => {
        this.guardandoEdicionCorte.delete(id);
        swal.fire("Error", "No se pudo guardar el corte.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Alta de un corte. El resultado es obligatorio, igual que en el backend. */
  agregarCorte(r: RiesgoProceso): void {
    // El [disabled] del HTML es la cortesia; esta guarda es la que de verdad
    // protege: el atributo se quita desde el inspector en dos clics.
    if (!this.enEdicion(r)) {
      return;
    }
    const id = r.idRiesgoProceso!;
    const nuevo = this.nuevoCorte[id];
    if (!nuevo || !nuevo.fechaMonitoreo) {
      swal.fire("Falta la fecha", "Indica la fecha del monitoreo.", "warning");
      return;
    }
    if (!nuevo.resultado || !nuevo.resultado.trim()) {
      swal.fire(
        "Falta el resultado",
        "El resultado es obligatorio. Si no se realizó la revisión, escribe NA y la razón.",
        "warning"
      );
      return;
    }
    const minimo = this.minNuevoCorte(r);
    if (nuevo.fechaMonitoreo! < minimo) {
      swal.fire(
        "Fecha no permitida",
        "El corte no puede quedar antes del " + this.aDia(minimo) +
          ": ni antes de hoy, ni antes del último corte registrado.",
        "warning"
      );
      return;
    }
    nuevo.idRiesgoProceso = id;
    this.guardandoCorte.add(id);
    this.cdr.detectChanges();
    this.service.guardarSeguimientoRiesgo(nuevo).subscribe({
      next: (res) => {
        this.guardandoCorte.delete(id);
        if (!res || res.codigoError) {
          swal.fire("No se guardó", (res && res.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        if (quedoComoPropuesta(res)) {
          void avisarPropuesta("El corte nuevo de " + (r.consecutivo || "este riesgo"));
        }
        // Este borrador ya se guardo; los de otras tarjetas sobreviven a la recarga.
        delete this.nuevoCorte[id];
        this.refrescarListas(id);
        this.cargar();
        this.alertaMonitoreo.refrescar();
      },
      error: () => {
        this.guardandoCorte.delete(id);
        swal.fire("Error", "No se pudo guardar el corte.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  private hoy(): string {
    const d = new Date();
    const mes = String(d.getMonth() + 1).padStart(2, "0");
    const dia = String(d.getDate()).padStart(2, "0");
    return d.getFullYear() + "-" + mes + "-" + dia;
  }
}
